import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileText, Download, Eye } from "lucide-react";
import { toast } from "sonner";
import { PaymentMethodBadge } from "@/components/PaymentMethodBadge";

const TYPE_LABEL = {
  PAYMENT_BREAKDOWN: "Desglose de pago",
  SETTLEMENT: "Liquidación",
  AUCTION_FEE: "Cobro de días de subasta",
};
const SETT_LABEL = {
  complete: "Completa",
  partial: "Parcial",
  full_refund: "Devolución total",
  cancelled_no_charge: "Cancelado sin cobro",
};
const STATE_CLASS = {
  RETAINED: "bg-amber-500/20 text-amber-300 border-amber-500/40",
  SETTLED_COMPLETE: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  SETTLED_PARTIAL: "bg-blue-500/20 text-blue-300 border-blue-500/40",
  REFUNDED_FULL: "bg-red-500/20 text-red-300 border-red-500/40",
  CANCELLED_NO_CHARGE: "bg-neutral-500/20 text-neutral-300 border-neutral-500/40",
  HITTCG_INCOME: "bg-red-500/20 text-red-300 border-red-500/40",
};

export function LedgerButtons({ orderId, paymentMethod }) {
  const [docs, setDocs] = useState([]);
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    if (!orderId) return;
    api.get(`/orders/${orderId}/ledger`).then(r => setDocs(r.data)).catch(() => setDocs([]));
  }, [orderId]);

  if (!docs.length) return null;

  const downloadPdf = async (doc) => {
    try {
      const r = await api.get(`/ledger/${doc.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${doc.doc_number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo descargar el PDF");
    }
  };

  return (
    <div className="bg-[#111] border border-[#262626] rounded-md p-4 mt-4" data-testid="ledger-panel">
      <div className="flex items-center gap-2 mb-3">
        <FileText size={16} className="text-[#EF4444]" />
        <h3 className="font-display text-lg">Contabilidad del pedido</h3>
        <span className="ml-auto text-xs text-neutral-500">{docs.length} documento{docs.length === 1 ? "" : "s"}</span>
      </div>
      <div className="space-y-2">
        {docs.map(d => (
          <div key={d.id} data-testid={`ledger-doc-${d.id}`} className="flex items-center gap-3 bg-[#0A0A0A] border border-[#262626] rounded p-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-xs text-neutral-400">{d.doc_number}</span>
                <span className={`text-[10px] uppercase px-2 py-0.5 rounded border ${STATE_CLASS[d.money_state] || ""}`}>{d.money_state}</span>
              </div>
              <div className="text-sm text-neutral-200 mt-1">
                {TYPE_LABEL[d.type]}{d.settlement_type ? ` — ${SETT_LABEL[d.settlement_type]}` : ""}
              </div>
              <div className="text-[11px] text-neutral-500">{new Date(d.created_at).toLocaleString("es-EC")}</div>
            </div>
            <Button data-testid={`ledger-view-${d.id}`} size="sm" variant="ghost" onClick={() => setViewing(d)} className="text-neutral-300 hover:text-white">
              <Eye size={14} className="mr-1" />Abrir
            </Button>
            <Button data-testid={`ledger-download-${d.id}`} size="sm" variant="outline" onClick={() => downloadPdf(d)} className="border-[#EF4444]/50 text-[#EF4444] hover:bg-[#EF4444]/10">
              <Download size={14} className="mr-1" />PDF
            </Button>
          </div>
        ))}
      </div>
      <LedgerViewerDialog doc={viewing} paymentMethod={paymentMethod} onClose={() => setViewing(null)} />
    </div>
  );
}

export function LedgerViewerDialog({ doc, paymentMethod, onClose }) {
  if (!doc) return null;
  const a = doc.amounts || {};
  // Método de pago: preferimos el de la orden (prop) por si no está guardado
  // en el snapshot del doc del ledger (docs viejos pre-Feb 2026).
  const method = paymentMethod || a.payment_method;
  const isPB = doc.type === "PAYMENT_BREAKDOWN";
  const isSettlement = doc.type === "SETTLEMENT";
  const isFee = doc.type === "AUCTION_FEE";
  return (
    <Dialog open={!!doc} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="ledger-viewer" className="bg-[#111] border-[#262626] text-white max-w-2xl max-h-[calc(100dvh-4rem)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <FileText size={20} className="text-[#EF4444]" />
            {TYPE_LABEL[doc.type]}{doc.settlement_type ? ` — ${SETT_LABEL[doc.settlement_type]}` : ""}
          </DialogTitle>
        </DialogHeader>
        <div className="text-xs text-neutral-500 mb-3">
          Documento interno de control · Inmutable · No es comprobante fiscal del SRI
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Field label="N° documento" value={doc.doc_number} mono />
          {doc.order_id && <Field label="Pedido" value={`#${doc.order_id.slice(0, 12)}`} mono />}
          {doc.source === "auction" && <Field label="Origen" value={`Subasta #${(doc.auction_id || "").slice(0, 12)}`} mono />}
          <Field label="Fecha" value={new Date(doc.created_at).toLocaleString("es-EC")} />
          <Field label="Estado dinero" value={doc.money_state} highlight={STATE_CLASS[doc.money_state]} />
          {isPB && doc.payphone_tx_id != null && <Field label="ID Payphone" value={doc.payphone_tx_id} mono />}
          {doc.related_doc_id && <Field label="Referencia pago" value={doc.related_doc_id.slice(0, 8)} mono />}
        </div>

        {doc.item && (
          <div className="mt-4 p-3 bg-[#0A0A0A] rounded border border-[#262626]">
            <div className="text-xs text-neutral-500 uppercase mb-2">Ítem</div>
            <div className="text-sm text-neutral-200">{doc.item.card_name} · {(doc.item.language || "").toUpperCase()} · {doc.item.variant} · {doc.item.condition || "—"} · x{doc.item.quantity}</div>
          </div>
        )}

        <div className="mt-4 p-3 bg-[#0A0A0A] rounded border border-[#262626]">
          <div className="text-xs text-neutral-500 uppercase mb-2">Montos</div>
          {isFee ? (
            <div className="grid grid-cols-2 gap-2 text-sm" data-testid="ledger-fee-amounts">
              <Mon label="Monto bruto cobrado" value={a.gross_paid_by_seller} highlight="text-white font-bold" />
              <Mon label="Comisión Payphone" value={a.payphone_fee} />
              <Mon label="Neto HitTCG (tarifa por días)" value={a.hittcg_net} highlight="text-emerald-400" />
              <div><div className="text-[10px] text-neutral-500 uppercase">Duración · Método</div><div className="text-sm">{a.duration_days} día(s) · {doc.payment_method === "transfer" ? "Transferencia" : doc.payment_method === "payphone" ? "Tarjeta (Payphone)" : "Tarjeta (simulado)"}</div></div>
              {doc.payment_ref && <div className="col-span-2"><div className="text-[10px] text-neutral-500 uppercase">Referencia de pago</div><div className="font-mono text-xs">{doc.payment_ref}</div></div>}
              <div><div className="text-[10px] text-neutral-500 uppercase">Cuadre</div><div className={`text-sm font-bold ${a.balances ? "text-emerald-400" : "text-red-400"}`}>{a.balances ? "SÍ cuadra ✓" : "NO cuadra — revisar"}</div></div>
            </div>
          ) : isPB ? (
            <div className="grid grid-cols-2 gap-2 text-sm">
              <Mon label="Precio de venta" value={a.price} />
              <Mon label="Envío" value={a.ship_cost} />
              {a.hittcg_commission !== undefined && <Mon label="Comisión HitTCG" value={a.hittcg_commission} />}
              {a.payphone_fee !== undefined && <Mon label="Comisión Payphone" value={a.payphone_fee} />}
              {method && (
                <div className="col-span-2">
                  <div className="text-[10px] text-neutral-500 uppercase mb-0.5">Método de pago</div>
                  <PaymentMethodBadge method={method} size="sm" />
                  {method === "transfer" && Number(a.payphone_fee || 0) === 0 && (
                    <div className="text-[10px] text-blue-300/80 italic mt-1">
                      Sin comisión Payphone (transferencia bancaria).
                    </div>
                  )}
                </div>
              )}
              <Mon label="Total pagado" value={a.gross_paid_by_buyer} highlight="text-white font-bold" />
              {a.seller_net_theoretical !== undefined && <Mon label="Neto teórico vendedor" value={a.seller_net_theoretical} highlight="text-emerald-400" />}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2 text-sm">
              <Mon label="Total original (bruto)" value={a.gross_original} highlight="text-white" />
              {a.seller_paid !== undefined && <Mon label="Pagado al vendedor" value={a.seller_paid} highlight="text-emerald-400" />}
              <Mon label="Devuelto al comprador" value={a.buyer_refunded} highlight={a.buyer_refunded > 0 ? "text-red-400" : ""} />
              {a.hittcg_kept !== undefined && <Mon label="Retenido HitTCG (comisión)" value={a.hittcg_kept} />}
              {a.payphone_kept !== undefined && <Mon label="Retenido Payphone" value={a.payphone_kept} />}
              {method && (
                <div className="col-span-2">
                  <div className="text-[10px] text-neutral-500 uppercase mb-0.5">Método de pago</div>
                  <PaymentMethodBadge method={method} size="sm" />
                </div>
              )}
              {a.balances !== undefined && (
                <div>
                  <div className="text-[10px] text-neutral-500 uppercase">Cuadre</div>
                  <div className={`text-sm font-bold ${a.balances ? "text-emerald-400" : "text-red-400"}`}>{a.balances ? "SÍ cuadra ✓" : "NO cuadra — revisar"}</div>
                </div>
              )}
            </div>
          )}
        </div>

        {isSettlement && doc.transfer && (doc.transfer.id || doc.transfer.date) && (
          <div className="mt-3 p-3 bg-emerald-500/5 rounded border border-emerald-500/30 text-sm">
            <div className="text-emerald-300 font-semibold mb-1">Transferencia al vendedor</div>
            <div className="text-xs text-neutral-300">
              <b>ID/N°:</b> <span className="font-mono">{doc.transfer.id || "—"}</span> · <b>Fecha:</b> {doc.transfer.date || "—"}
            </div>
          </div>
        )}

        {doc.reason && (
          <div className="mt-3 p-3 bg-[#0A0A0A] rounded border border-[#262626] text-sm">
            <div className="text-xs text-neutral-500 uppercase mb-1">Motivo / notas</div>
            <div className="text-neutral-200">{doc.reason}</div>
          </div>
        )}

        <div className="mt-4 text-[11px] text-neutral-500 border-t border-[#262626] pt-3">
          Documento inmutable. Cualquier corrección genera un nuevo asiento — este documento no se sobrescribe ni se elimina.
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, mono, highlight }) {
  return (
    <div>
      <div className="text-[10px] text-neutral-500 uppercase tracking-wider">{label}</div>
      <div className={`text-sm mt-0.5 ${mono ? "font-mono" : ""} ${highlight ? `inline-block px-2 py-0.5 rounded border ${highlight}` : "text-neutral-200"}`}>{value}</div>
    </div>
  );
}

function Mon({ label, value, highlight }) {
  return (
    <div>
      <div className="text-[10px] text-neutral-500 uppercase">{label}</div>
      <div className={`text-base ${highlight || "text-neutral-200"}`}>${Number(value ?? 0).toFixed(2)}</div>
    </div>
  );
}
